import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Typography,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Mail as MailIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  fetchTransactions,
  setTransactionFilters,
  setCurrentPage,
  Transaction,
} from '../../store/slices/merchantSlice';
import api from '../../services/api';

const MerchantTransactionsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { transactions, currentPage, pageSize, totalTransactions, transactionFilters, loadingTransactions } = useSelector(
    (state: RootState) => state.merchant
  );

  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState<string | null>(null);
  const [receiptDialog, setReceiptDialog] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = () => {
    dispatch(fetchTransactions({
      page: currentPage,
      limit: pageSize,
      status: statusFilter,
      startDate,
      endDate,
    }));
  };

  const handleFilterChange = () => {
    dispatch(setCurrentPage(1));
    loadTransactions();
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setCurrentPage(newPage + 1));
    dispatch(fetchTransactions({
      page: newPage + 1,
      limit: pageSize,
      status: statusFilter,
      startDate,
      endDate,
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setCurrentPage(1));
    dispatch(fetchTransactions({
      page: 1,
      limit: parseInt(event.target.value, 10),
      status: statusFilter,
      startDate,
      endDate,
    }));
  };

  const handleOpenReceiptDialog = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setReceiptEmail('');
    setReceiptDialog(true);
  };

  const handleSendReceipt = async () => {
    if (!selectedTransactionId || !receiptEmail) return;

    try {
      setSendingReceipt(selectedTransactionId);
      await api.post('/store/receipts/send', {
        transactionId: selectedTransactionId,
        email: receiptEmail,
      });
      setReceiptDialog(false);
      // Show success notification (integrate with your notification system)
      alert('Receipt sent successfully!');
    } catch (error: any) {
      alert(error.response?.data?.error?.message || 'Failed to send receipt');
    } finally {
      setSendingReceipt(null);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 700,
          color: '#202647',
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}>
          Transaction History
        </Typography>
        <Typography variant="body2" sx={{ color: '#7F839C', mt: 0.5 }}>
          View and manage all your transactions
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Grid container spacing={2} sx={{ alignItems: 'flex-end' }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Status"
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">All Status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFilterChange}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      {loadingTransactions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
          <Typography variant="body2" sx={{ color: '#7F839C' }}>
            No transactions found. {statusFilter ? 'Try adjusting your filters.' : 'Create a payment link to get started!'}
          </Typography>
        </Card>
      ) : (
        <>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 16px rgba(32, 38, 71, 0.06)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8F8FC' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Payer</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#202647' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => (
                    <TableRow key={transaction.id} sx={{ '&:hover': { backgroundColor: '#F8F8FC' } }}>
                      <TableCell sx={{ color: '#565972', fontWeight: 500 }}>
                        {transaction.reference}
                      </TableCell>
                      <TableCell sx={{ color: '#565972', fontWeight: 600 }}>
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          color={getStatusColor(transaction.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#7F839C', fontSize: '0.9rem' }}>
                        {transaction.senderName || transaction.senderEmail || '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#7F839C', fontSize: '0.85rem' }}>
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<MailIcon fontSize="small" />}
                          onClick={() => handleOpenReceiptDialog(transaction.id)}
                          disabled={sendingReceipt === transaction.id}
                        >
                          {sendingReceipt === transaction.id ? 'Sending...' : 'Receipt'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={totalTransactions}
                rowsPerPage={pageSize}
                page={currentPage - 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Grid container spacing={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {transactions.map((transaction) => (
              <Grid size={{ xs: 12 }} key={transaction.id}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#7F839C', fontSize: '0.75rem' }}>
                        Reference
                      </Typography>
                      <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                        {transaction.reference}
                      </Typography>
                    </Box>

                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" sx={{ color: '#7F839C' }}>
                          Amount
                        </Typography>
                        <Typography sx={{ fontWeight: 600, color: '#202647' }}>
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" sx={{ color: '#7F839C' }}>
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={transaction.status}
                            color={getStatusColor(transaction.status) as any}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    {transaction.senderName && (
                      <Typography variant="caption" sx={{ color: '#7F839C', display: 'block', mb: 1 }}>
                        Payer: {transaction.senderName}
                      </Typography>
                    )}

                    <Typography variant="caption" sx={{ color: '#7F839C', display: 'block', mb: 1.5 }}>
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </Typography>

                    <Button
                      fullWidth
                      size="small"
                      startIcon={<MailIcon fontSize="small" />}
                      onClick={() => handleOpenReceiptDialog(transaction.id)}
                      disabled={sendingReceipt === transaction.id}
                    >
                      {sendingReceipt === transaction.id ? 'Sending Receipt...' : 'Send Receipt'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Receipt Dialog */}
      <Dialog
        open={receiptDialog}
        onClose={() => setReceiptDialog(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#202647', bgcolor: '#F8F8FC' }}>
          Send Receipt
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#7F839C', mb: 2 }}>
            Enter the email address where you want to send the receipt.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={receiptEmail}
            onChange={(e) => setReceiptEmail(e.target.value)}
            placeholder="customer@example.com"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #E8E8F0' }}>
          <Button onClick={() => setReceiptDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSendReceipt}
            variant="contained"
            disabled={!receiptEmail}
          >
            Send Receipt
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MerchantTransactionsPage;
